import axios from "axios";
import { notification } from "antd";
import download from "downloadjs";
import { useSelector } from "react-redux";
import { getRequest, postRequest, putRequest } from "../../core/network";
import endpoints from "../../constants/endPoints.json";

const paths = () => endpoints.whFinance.os;

const cleanQuery = (query) => {
  if (!query) return undefined;
  const next = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") next[key] = value;
  });
  return Object.keys(next).length ? next : undefined;
};

export const errorMessage = (err) => {
  const data = err?.response?.data;
  if (data && typeof data === "object" && !(data instanceof ArrayBuffer) && data.message) return data.message;
  if (data instanceof ArrayBuffer) {
    try {
      const parsed = JSON.parse(new TextDecoder().decode(data));
      if (parsed?.message) return parsed.message;
    } catch (parseErr) {
      /* binary file errors stay generic */
    }
  }
  return "Request failed";
};

async function call(method, token, endpoint, { query, body, params, silent } = {}) {
  try {
    const cleaned = cleanQuery(query);
    const response = method === "get"
      ? await getRequest(cleaned, params, token, endpoint)
      : method === "post"
        ? await postRequest(body ?? {}, cleaned, params, token, endpoint)
        : await putRequest(body ?? {}, cleaned, params, token, endpoint);
    return response.data;
  } catch (err) {
    if (!silent) notification.error({ message: errorMessage(err) });
    throw err;
  }
}

export function useFinanceToken() {
  return useSelector((state) => state.auth?.token);
}

export function loadVendors(token) {
  return getRequest(undefined, undefined, token, endpoints.whFinance.vendors)
    .then((response) => {
      const data = response.data;
      if (Array.isArray(data)) return data;
      return data?.vendors || data?.docs || [];
    })
    .catch((err) => {
      notification.error({ message: errorMessage(err) });
      return [];
    });
}

export async function downloadOs(token, endpoint, query, fallbackName) {
  const base = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
  const target = endpoint.startsWith("http") ? endpoint : `${base}/${endpoint.replace(/^\//, "")}`;
  try {
    const response = await axios.get(target, {
      params: endpoint.startsWith("http") || endpoint.includes("?") ? undefined : cleanQuery(query),
      headers: { "x-access-token": token, Accept: "json" },
      responseType: "arraybuffer",
    });
    const disposition = response.headers["content-disposition"] || "";
    const named = disposition.match(/filename="?([^"]+)"?/);
    const name = response.headers.filename || named?.[1] || fallbackName || "download";
    download(response.data, name, response.headers["content-type"] || "application/octet-stream");
  } catch (err) {
    notification.error({ message: errorMessage(err) });
    throw err;
  }
}

const get = (token, endpoint, query, params, silent) => call("get", token, endpoint, { query, params, silent });
const post = (token, endpoint, body, params) => call("post", token, endpoint, { body, params });
const put = (token, endpoint, body, params) => call("put", token, endpoint, { body, params });

export const os = {
  dashboard: (token, query) => get(token, paths().dashboard, query),
  search: (token, q) => get(token, paths().search, { q }, undefined, true),
  unreadCount: (token) => get(token, paths().notificationsUnread, undefined, undefined, true),
  notifications: (token, query) => get(token, paths().notifications, query),
  markRead: (token, id) => post(token, paths().notifications, {}, `/${id}/read`),
  markAllRead: (token) => post(token, paths().notifications, {}, "/read-all"),
  accounts: (token, query) => get(token, paths().accounts, query),
  createAccount: (token, body) => post(token, paths().accounts, body),
  updateAccount: (token, id, body) => put(token, paths().accounts, body, `/${id}`),
  memberships: (token, query) => get(token, paths().memberships, query),
  membershipSummary: (token) => get(token, paths().membershipsSummary),
  createMembership: (token, body) => post(token, paths().memberships, body),
  overstockStock: (token, vendorId) => get(token, paths().overstockStock, { vendorId }),
  overstockPreview: (token, body) => post(token, paths().overstockPreview, body),
  overstockRuns: (token, query) => get(token, paths().overstockRuns, query),
  overstockRun: (token, id) => get(token, paths().overstockRuns, undefined, `/${id}`),
  saveOverstockRun: (token, body) => post(token, paths().overstockRuns, body),
  overstockStatus: (token, id, status) => put(token, paths().overstockRuns, { status }, `/${id}/status`),
  overstockInvoice: (token, id, body) => post(token, paths().overstockRuns, body || {}, `/${id}/invoice`),
  overstockHistory: (token) => get(token, paths().overstockHistory),
  analysis: (token, months) => get(token, paths().analysis, { months }),
  expenses: (token, query) => get(token, paths().expenses, query),
  createExpense: (token, body) => post(token, paths().expenses, body),
  cashBank: (token, query) => get(token, paths().cashBank, query),
  saveCashBank: (token, body) => put(token, paths().cashBank, body),
  addCashLine: (token, body) => post(token, paths().cashBank, body, "/lines"),
  reconcile: (token, body) => post(token, paths().cashBankReconcile, body || {}),
  report: (token, type, query) => get(token, paths().reports, query, `/${type}`),
  downloadReport: (token, type, query) => downloadOs(token, `${paths().reports}/${type}/export`, query, `${type}.${query.format === "pdf" ? "pdf" : "xlsx"}`),
  audit: (token, query) => get(token, paths().audit, query),
  invoices: (token, query) => get(token, paths().invoices, query),
  createInvoice: (token, body) => post(token, paths().invoices, body),
  updateInvoice: (token, id, body) => put(token, paths().invoices, body, `/${id}`),
  invoiceAction: (token, id, action, body) => post(token, paths().invoices, body || {}, `/${id}/${action}`),
  downloadInvoice: (token, id, number) => downloadOs(token, `${paths().invoices}/${id}/pdf`, undefined, `${number || "invoice"}.pdf`),
  payouts: (token, query) => get(token, paths().payouts, query),
  payout: (token, id) => get(token, paths().payouts, undefined, `/${id}`),
  createPayout: (token, body) => post(token, paths().payouts, body),
  movePayout: (token, id, body) => post(token, paths().payouts, body, `/${id}/stage`),
  duePayouts: (token, query) => get(token, paths().payoutsDue, query),
  setDueDate: (token, vendorId, dueDate) => put(token, paths().payoutsDue, { dueDate }, `/${vendorId}`),
  recordDuePayment: (token, vendorId, body) => post(token, paths().payoutsDue, body, `/${vendorId}/payment`),
  remindDue: (token, vendorId) => post(token, paths().payoutsDue, {}, `/${vendorId}/remind`),
  remindAllDue: (token) => post(token, paths().payoutsDue, {}, "/remind-all"),
  escalateDue: (token, vendorId, note) => post(token, paths().payoutsDue, { note }, `/${vendorId}/escalate`),
  brandFees: (token, query) => get(token, paths().brandServiceFees, query),
  updateBrandRates: (token, vendorId, body) => put(token, paths().brandServiceFees, body, `/${vendorId}`),
  tiers: (token, query) => get(token, paths().serviceFeeTiers, query),
  tierRevenue: (token, months) => get(token, paths().serviceFeeTierRevenue, { months }),
  duplicateTier: (token, id) => post(token, paths().serviceFeeTiers, {}, `/${id}/duplicate`),
  archiveTier: (token, id) => post(token, paths().serviceFeeTiers, {}, `/${id}/archive`),
  restoreTier: (token, id) => post(token, paths().serviceFeeTiers, {}, `/${id}/restore`),
  tierHistory: (token, id) => get(token, paths().serviceFeeTiers, undefined, `/${id}/history`),
};
